import { useParams, useNavigate, useLocation } from "react-router";
import { useMemo, useCallback, useEffect } from "react";
import { encodeId, parseRouteId } from "../utils/idObfuscation";

/**
 * Check if a string is a raw numeric ID (not encoded)
 */
function isRawNumericId(value: string | undefined): boolean {
  if (!value) return false;
  const asNumber = parseInt(value, 10);
  return !isNaN(asNumber) && asNumber >= 0 && String(asNumber) === value;
}

/**
 * Drop-in replacement for useParams that automatically decodes obfuscated IDs.
 * Returns string values with numeric IDs decoded back to their original numbers.
 *
 * @example
 * // URL: /lease/Xk9mP2qR/pets/J3nK8vLm
 * const { leaseId, id } = useRouteParams();
 * // leaseId = "64633", id = "123" (decoded strings, ready to use)
 *
 * // For numeric values, parse them:
 * const leaseIdNum = Number(leaseId); // 64633
 */
export function useRouteParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>
>(): T {
  const params = useParams();

  return useMemo(() => {
    const decoded: Record<string, string | undefined> = {};

    for (const [key, value] of Object.entries(params)) {
      if (value === undefined) {
        decoded[key] = undefined;
        continue;
      }

      // Try to decode as an obfuscated ID
      const numericId = parseRouteId(value);
      if (numericId !== null) {
        decoded[key] = String(numericId);
      } else {
        // Keep as-is (non-numeric params like slugs)
        decoded[key] = value;
      }
    }

    return decoded as T;
  }, [params]);
}

/**
 * Hook to get a decoded ID from route parameters
 * @param paramName - The name of the route parameter
 * @returns The decoded numeric ID, or null if invalid
 */
export function useDecodedParam(paramName: string): number | null {
  const params = useParams();
  const encodedValue = params[paramName];

  return useMemo(() => {
    return parseRouteId(encodedValue);
  }, [encodedValue]);
}

/**
 * Hook to get multiple decoded IDs from route parameters
 * @param paramNames - Array of route parameter names
 * @returns Object mapping param names to decoded numeric IDs
 */
export function useDecodedParams<T extends string>(
  paramNames: T[],
): Record<T, number | null> {
  const params = useParams();

  return useMemo(() => {
    return paramNames.reduce(
      (acc, name) => {
        acc[name] = parseRouteId(params[name]);
        return acc;
      },
      {} as Record<T, number | null>,
    );
  }, [params, paramNames]);
}

/**
 * Hook that provides navigation with encoded IDs
 * @returns Object with navigate functions that encode IDs
 */
export function useEncodedNavigate() {
  const navigate = useNavigate();

  const navigateWithEncodedId = useCallback(
    (basePath: string, id: number, options?: { replace?: boolean }) => {
      const encodedId = encodeId(id);
      navigate(`${basePath}/${encodedId}`, options);
    },
    [navigate],
  );

  const navigateWithEncodedIds = useCallback(
    (
      pathTemplate: string,
      ids: Record<string, number>,
      options?: { replace?: boolean },
    ) => {
      let path = pathTemplate;
      Object.entries(ids).forEach(([key, value]) => {
        path = path.replace(`:${key}`, encodeId(value));
      });
      navigate(path, options);
    },
    [navigate],
  );

  return {
    navigateWithEncodedId,
    navigateWithEncodedIds,
  };
}

/**
 * Utility to build a path with encoded IDs
 * @param pathTemplate - Path template with :paramName placeholders
 * @param ids - Object mapping param names to numeric IDs
 * @returns The path with encoded IDs
 *
 * Example:
 *   buildEncodedPath("/lease/:leaseId/pets/:petId", { leaseId: 123, petId: 456 })
 *   => "/lease/K4x8hM/pets/N9y2pQ"
 */
export function buildEncodedPath(
  pathTemplate: string,
  ids: Record<string, number>,
): string {
  let path = pathTemplate;
  Object.entries(ids).forEach(([key, value]) => {
    path = path.replace(`:${key}`, encodeId(value));
  });
  return path;
}

/**
 * Higher-order component wrapper for route components that need decoded IDs
 * Provides decoded IDs as props to the wrapped component
 */
export function withDecodedParams<P extends object>(
  WrappedComponent: React.ComponentType<
    P & { decodedIds: Record<string, number | null> }
  >,
  paramNames: string[],
) {
  return function WithDecodedParamsWrapper(props: P) {
    const params = useParams();
    const decodedIds = useMemo(() => {
      return paramNames.reduce(
        (acc, name) => {
          acc[name] = parseRouteId(params[name]);
          return acc;
        },
        {} as Record<string, number | null>,
      );
    }, [params]);

    return <WrappedComponent {...props} decodedIds={decodedIds} />;
  };
}

// Re-export the utility functions for convenience
export {
  encodeId,
  decodeId,
  parseRouteId,
  encodeIds,
  decodeIds,
  isValidEncodedId,
} from "../utils/idObfuscation";

/**
 * Hook that redirects from legacy numeric ID URLs to encoded ID URLs
 * Use this in route components to gradually migrate old bookmarked/emailed links
 *
 * @param paramNames - Array of route parameter names that should be encoded
 * @param options - Configuration options
 * @param options.enabled - Whether redirects are enabled (default: true)
 * @param options.trackRedirect - Optional callback when a redirect occurs (for analytics)
 * @returns Object with isRedirecting flag and decoded params
 *
 * @example
 * // In a route component:
 * const { isRedirecting, params } = useEncodedIdRedirect(['leaseId', 'ticketId']);
 * if (isRedirecting) return null; // or a loading spinner
 */
export function useEncodedIdRedirect<T extends string>(
  paramNames: T[],
  options: {
    enabled?: boolean;
    trackRedirect?: (from: string, to: string) => void;
  } = {},
): { isRedirecting: boolean; params: Record<T, number | null> } {
  const { enabled = true, trackRedirect } = options;
  const routeParams = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if any params are raw numeric IDs that need encoding
  const needsRedirect = useMemo(() => {
    if (!enabled) return false;
    return paramNames.some((name) => isRawNumericId(routeParams[name]));
  }, [enabled, paramNames, routeParams]);

  // Build the encoded URL
  const encodedPath = useMemo(() => {
    if (!needsRedirect) return null;

    let newPath = location.pathname;

    paramNames.forEach((name) => {
      const value = routeParams[name];
      if (isRawNumericId(value)) {
        const numericId = parseInt(value!, 10);
        const encoded = encodeId(numericId);
        // Replace the numeric ID in the path with the encoded version
        newPath = newPath.replace(`/${value}`, `/${encoded}`);
      }
    });

    // Preserve search params and hash
    return newPath + location.search + location.hash;
  }, [needsRedirect, location.pathname, location.search, location.hash, paramNames, routeParams]);

  // Perform the redirect
  useEffect(() => {
    if (needsRedirect && encodedPath) {
      trackRedirect?.(location.pathname, encodedPath);
      navigate(encodedPath, { replace: true });
    }
  }, [needsRedirect, encodedPath, navigate, trackRedirect, location.pathname]);

  // Parse the params (works for both old numeric and new encoded formats)
  const params = useMemo(() => {
    return paramNames.reduce(
      (acc, name) => {
        acc[name] = parseRouteId(routeParams[name]);
        return acc;
      },
      {} as Record<T, number | null>,
    );
  }, [paramNames, routeParams]);

  return {
    isRedirecting: needsRedirect,
    params,
  };
}
